import getServices, {
    type RequestedServices,
    type ServicesRequest,
} from '@conduit-client/service-provisioner/v1';
import { toError } from '@conduit-client/utils';
import type { Command } from '@conduit-client/command-base/v1';
import type {
    Result,
    SyncOrAsync,
    SubscribableResult,
    Callback,
    IInternalError,
    IUserVisibleError,
    ServiceDescriptor,
} from '@conduit-client/utils';

export type ResultOrSubscribableResult<Data, Error = unknown> =
    | Result<Data, Error>
    | SubscribableResult<Data, Error>;

export type MaybeSubscribableResultCommand<Data, Error = unknown> = Command<
    SyncOrAsync<ResultOrSubscribableResult<Data, Error>>
>;

export type SubscribableResultCommand<
    Data,
    Error = unknown,
    Params extends Array<unknown> = [],
> = Command<SyncOrAsync<SubscribableResult<Data, Error>>, Params>;

export type ResultCommand<Data, Error = unknown> = Command<SyncOrAsync<Result<Data, Error>>>;

export function isInternalError<Data = undefined>(error: unknown): error is IInternalError<Data> {
    return error instanceof Error && 'type' in error && error.type === 'internal';
}

export function isUserVisibleError<Data = undefined>(
    error: unknown
): error is IUserVisibleError<Data> {
    return error instanceof Error && 'type' in error && error.type === 'user-visible';
}

export function throwUserlandError(error: unknown): never {
    logError(error);
    throw buildUserlandError(error);
}

export function emitError(
    callback: Callback<{ data: undefined; error: Error }>,
    error: unknown
): void {
    logError(error);
    callback({ data: undefined, error: buildUserlandError(error) });
}

export function buildUserlandError(error: unknown): Error {
    if (isUserVisibleError<Error>(error)) {
        return error.data;
    }

    return new Error('Internal error in Lightning Data Service adapter occurred.');
}

/**
 * Unwraps a UserVisibleError to its underlying `.data` payload (e.g. a
 * FetchResponse carrying status/body), while passing every other error through
 * unchanged via {@link toError}.
 *
 * This is the imperative-bindings counterpart to {@link buildUserlandError}.
 * Wire adapters mask unexpected errors behind a generic message (see
 * `buildUserlandError`); the imperative surface instead preserves the original
 * error so HTTP consumers keep the raw `Error(statusText)` / network-failure
 * message that the transport-error-shape-parity ADR documents as the default
 * contract. The only behavior change from the historical `toError(...)` path is
 * that a UserVisibleError is now unwrapped to its actionable `.data` instead of
 * leaking the empty-message wrapper.
 */
export function unwrapUserlandError(error: unknown): Error {
    if (isUserVisibleError<Error>(error)) {
        return error.data;
    }

    return toError(error);
}

export function throwUnwrappedUserlandError(error: unknown): never {
    throw unwrapUserlandError(error);
}

export function logError(error: unknown): void {
    if (isUserVisibleError(error)) {
        return;
    }

    console.error('OneStore Command threw an error that we did not expect', error);
}

export type InitBindingsExportsArgs<Req extends ServicesRequest<ServiceDescriptor<unknown>>> = {
    serviceRequirements: Req;
    init: (services: RequestedServices<Req>) => void;
    timeout?: number;
};

/**
 * Bootstraps adapter bindings by requesting services from the service provisioner and
 * invoking the initializer once all services are available.
 */
export function initBindingsExports<Req extends ServicesRequest<ServiceDescriptor<unknown>>>(
    args: InitBindingsExportsArgs<Req>
): void {
    const { serviceRequirements, init, timeout } = args;

    getServices(serviceRequirements, timeout).then((services) => {
        init(services);
    });
}
